/**
 * SOLY-SUPPLIER-002 MS-04 — what the person agrees to when adding a mailbox (MUST-11, avenant IA
 * Annexe A). The checkbox label is the whole notice: what is read, what is not, and the AI in
 * Europe — no paragraph around it (Thierry, 2026-10-06). Frozen by a snapshot test: any wording
 * change goes through legal review.
 */
export const MAILBOX_CONSENT_LABEL =
  'J’accepte que Soly lise les en-têtes de cette boîte professionnelle (jamais le texte des emails) pour me proposer mes fournisseurs, avec une IA hébergée en Europe.'

/** Shown only when the address is not the one the person signs in to Soly with. */
export const MAILBOX_HOLDER_INFORMED_LABEL = 'La personne qui utilise cette boîte est prévenue.'

/**
 * True when the typed address is someone else's than the Soly user's own: the second checkbox
 * (« La personne … est prévenue ») is then required. An empty address asks nothing yet.
 */
export function needsHolderInformed(address: string, userEmail: string | null | undefined): boolean {
  const typed = address.trim().toLowerCase()
  if (!typed) return false
  return typed !== (userEmail ?? '').trim().toLowerCase()
}

// ── SOLY-SUPPLIER-002 MS-05 — when Microsoft asks for an administrator's consent ─────────────────

/** Shown after a failed return from Microsoft. Conditional on purpose: Soly cannot know why Microsoft refused. */
export const ADMIN_CONSENT_FAILED_TEXT =
  'La connexion n’a pas abouti. Si Microsoft vous a demandé l’accord d’un administrateur, envoyez-lui ce message, puis réessayez quand il a accepté.'

/** Shown when the person opens the help without a failed attempt. */
export const ADMIN_CONSENT_HELP_TEXT =
  'Si Microsoft vous demande l’accord d’un administrateur, envoyez-lui ce message, puis réessayez quand il a accepté.'

export const ADMIN_CONSENT_HELP_LINK_LABEL = 'Microsoft demande l’accord de votre administrateur ?'

/**
 * The message the person sends their Microsoft administrator, by any channel. It says what the link
 * gives (the headers of the emails, never their content) — frozen by a snapshot test, like the consent.
 */
export function adminConsentMessage(domain: string, url: string): string {
  return [
    `Bonjour, pour connecter ma boîte mail à Soly, Microsoft demande l’accord d’un administrateur de ${domain}. Ce lien ouvre la page Microsoft où vous pouvez l’autoriser :`,
    url,
    'Soly lit les en-têtes des emails (expéditeur, destinataires, date, objet), jamais leur contenu ni les pièces jointes. Merci de me prévenir quand c’est fait.',
  ].join('\n')
}

/** Microsoft's « AADSTS… » code inside a text (its error description, or the API's message), or null. */
export function extractAadstsCode(text: string | null | undefined): string | null {
  return text?.match(/AADSTS\d+/)?.[0] ?? null
}

/** The Microsoft round trip in progress, kept for the tab's lifetime so a failed return can retry the same thing. */
export type MailboxAttempt = { address: string } | { mailboxId: string }

export const MAILBOX_ATTEMPT_KEY = 'soly.mailbox.attempt'

export function rememberAttempt(attempt: MailboxAttempt) {
  try {
    sessionStorage.setItem(MAILBOX_ATTEMPT_KEY, JSON.stringify(attempt))
  } catch {
    // Storage blocked: after a failure the person types the address again.
  }
}

/** The attempt, once: cleared when read. */
export function takeAttempt(): MailboxAttempt | null {
  try {
    const raw = sessionStorage.getItem(MAILBOX_ATTEMPT_KEY)
    sessionStorage.removeItem(MAILBOX_ATTEMPT_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : null
    if (parsed && typeof parsed === 'object') {
      if ('address' in parsed && typeof parsed.address === 'string') return { address: parsed.address }
      if ('mailboxId' in parsed && typeof parsed.mailboxId === 'string') return { mailboxId: parsed.mailboxId }
    }
    return null
  } catch {
    return null
  }
}
